import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Checkbox } from "../../../app/components/ui/checkbox";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { api } from "../../../lib/api";
import { buildCsvFromRows, downloadCsvFile } from "../../../lib/csv";
import { useAuth } from "../../../providers/AuthProvider";
import { useDealers } from "../hooks/useDealers";

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function DealerListPage() {
  const { token } = useAuth();
  const { dealers, loading, error, reload } = useDealers();
  const [q, setQ] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkWorking, setBulkWorking] = useState(false);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return dealers;
    return dealers.filter((d) => {
      return (
        d.name.toLowerCase().includes(qq) ||
        (d.contact_person ?? "").toLowerCase().includes(qq) ||
        (d.phone ?? "").toLowerCase().includes(qq) ||
        (d.email ?? "").toLowerCase().includes(qq) ||
        (d.gst_number ?? "").toLowerCase().includes(qq)
      );
    });
  }, [dealers, q]);

  const selectedCount = selectedIds.size;
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((d) => selectedIds.has(String(d.id)));
  const someFilteredSelected = filtered.some((d) => selectedIds.has(String(d.id)));

  const toggleRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const d of filtered) {
          if (checked) next.add(String(d.id));
          else next.delete(String(d.id));
        }
        return next;
      });
    },
    [filtered],
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const dealersToCsv = useCallback((list: typeof dealers) => {
    const header = [
      "id",
      "name",
      "contact_person",
      "phone",
      "email",
      "gst_number",
      "address",
      "status",
    ];
    const rows = list.map((d) => [
      String(d.id),
      d.name,
      d.contact_person ?? "",
      d.phone ?? "",
      d.email ?? "",
      d.gst_number ?? "",
      d.address ?? "",
      d.status ?? "",
    ]);
    return buildCsvFromRows(header, rows);
  }, []);

  const exportAll = () => {
    if (dealers.length === 0) return;
    downloadCsvFile(`dealers-export-all-${stamp()}.csv`, dealersToCsv(dealers));
  };

  const exportSelected = () => {
    const list = dealers.filter((d) => selectedIds.has(String(d.id)));
    if (list.length === 0) return;
    downloadCsvFile(`dealers-export-selected-${stamp()}.csv`, dealersToCsv(list));
  };

  const bulkDeleteSelected = async () => {
    if (!token || selectedIds.size === 0) return;
    const ok = window.confirm(`Delete ${selectedIds.size} dealer(s)? This cannot be undone.`);
    if (!ok) return;
    setBulkWorking(true);
    try {
      for (const id of Array.from(selectedIds)) {
        await api.dealers.remove({ token, id });
      }
      clearSelection();
      await reload();
    } finally {
      setBulkWorking(false);
    }
  };

  async function onDelete(id: string) {
    if (!token) return;
    const ok = window.confirm("Delete this dealer?");
    if (!ok) return;
    await api.dealers.remove({ token, id });
    await reload();
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">Dealers</h1>
          <p className="text-muted-foreground">Manage dealers for inventory entries.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={exportAll} disabled={loading || dealers.length === 0}>
            Export all
          </Button>
          <Button asChild>
            <Link to="/dealers/new">Add dealer</Link>
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All dealers</CardTitle>
          <CardDescription>Search and manage dealers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <Input
              placeholder="Search dealers..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-md"
            />
            <Button type="button" variant="outline" onClick={() => setQ("")}>
              Reset
            </Button>
          </div>

          {selectedCount > 0 ? (
            <div className="flex flex-wrap items-center gap-2 p-3 border rounded-md bg-muted/30">
              <span className="text-sm font-medium">{selectedCount} selected</span>
              <Button type="button" size="sm" variant="outline" onClick={clearSelection} disabled={bulkWorking}>
                Clear selection
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={exportSelected} disabled={bulkWorking}>
                Export selected
              </Button>
              <Button type="button" size="sm" variant="destructive" onClick={() => void bulkDeleteSelected()} disabled={bulkWorking}>
                Delete selected
              </Button>
            </div>
          ) : null}

          {error ? <div className="text-sm text-destructive">{error}</div> : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => toggleSelectAllFiltered(v === true)}
                    aria-label="Select all filtered dealers"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    No dealers found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(String(d.id))}
                        onCheckedChange={(v) => toggleRow(String(d.id), v === true)}
                        aria-label={`Select ${d.name}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell>
                      <div className="leading-tight">
                        <div>{d.contact_person || "-"}</div>
                        <div className="text-xs text-muted-foreground">{d.phone || d.email || "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell>{d.status ?? "-"}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/dealers/${encodeURIComponent(d.id)}/edit`}>Edit</Link>
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => void onDelete(d.id)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
